import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createUserAndCustomerApi,
  createUserForExistingCustomerApi,
} from 'api/adminApi';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import { ERROR_API_CODE } from 'constants/apiErrorsConstatns';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import validator from 'validator';

function AddUserModal({
  show,
  header,
  selectedCustomerId,
  onSubmitSuccess,
  onModalClose,
  companyNames,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState('Treisar');
  const toast = useToast();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors },
  } = useForm();

  const watchedEmail = watch('email');
  const watchedCompanyName = watch('companyName');

  useEffect(() => {
    if (show) {
      onOpen();
    }
  }, [show]);

  useEffect(() => {
    let defaultValues = {
      email: '',
    };

    if (!selectedCustomerId) {
      defaultValues = {
        ...defaultValues,
        companyName: '',
        googleDrivePath: '',
      };
    }
    reset(defaultValues);
    setTenant('Treisar');
  }, [selectedCustomerId]);

  const isFormFilled = () => {
    if (selectedCustomerId) {
      return Boolean(watchedEmail?.trim());
    }
    return Boolean(watchedCompanyName?.trim());
  };

  const close = () => {
    onClose();
    onModalClose();
  };

  const onSubmit = (data) => {
    const apiFunc = selectedCustomerId
      ? () =>
          createUserForExistingCustomerApi({
            customerId: selectedCustomerId,
            email: data.email,
          })
      : () =>
          createUserAndCustomerApi({
            email: data.email,
            companyName: data.companyName,
            tenant,
            googleDrivePath: data.googleDrivePath,
          });

    setLoading(true);
    apiFunc()
      .then((data) => {
        onSubmitSuccess({customerId: data?.customerId});
        close();
      })
      .catch((error) => {
        if (
          error.response?.data?.errorCode === ERROR_API_CODE.USER_ALREADY_EXISTS
        ) {
          setError('email', {
            type: 'manual',
            message: 'Email already exists',
          });
        } else {
          toast({
            title: 'Error',
            description: 'Error creating user',
            status: 'error',
          });
        }
        throw error;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <ModalOverlay />
      <ModalContent>
        <OverlaySpinnerWrapper show={loading}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader>{header}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl isInvalid={errors.email || errors.companyName}>
                <Stack spacing={5}>
                  {!selectedCustomerId && (
                    <Box>
                      <FormLabel>Tenant</FormLabel>
                      <Select
                        value={tenant}
                        onChange={(e) => setTenant(e.target.value)}
                      >
                        <option value="Treisar">Treisar</option>
                        <option value="Twelve">Twelve</option>
                      </Select>
                    </Box>
                  )}
                  <Box>
                    <FormLabel>Email (optional)</FormLabel>
                    <Input
                      {...register('email', {
                        validate: (value) => {
                          if (!value?.trim()) {
                            return true;
                          }
                          return validator.isEmail(value) || 'Email is not valid';
                        },
                      })}
                      type={'email'}
                    />
                    {errors.email?.message && (
                      <FormErrorMessage>
                        {errors.email.message}
                      </FormErrorMessage>
                    )}
                  </Box>
                  {!selectedCustomerId && (
                    <>
                    <Box>
                      <FormLabel>Company Name</FormLabel>
                      <Input
                        {...register('companyName', {
                          required: true,
                          validate: (value) => {
                            return (
                              !companyNames.includes(value) ||
                              'Company name already exists'
                            );
                          },
                        })}
                      />
                      {errors.companyName?.message && (
                        <FormErrorMessage>
                          {errors.companyName.message}
                        </FormErrorMessage>
                      )}
                    </Box>
                    <Box>
                      <FormLabel>Google Drive Path (optional)</FormLabel>
                      <Input
                        {...register('googleDrivePath')}
                        placeholder="Folder URL or ID"
                      />
                    </Box>
                    </>
                  )}
                </Stack>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                variant={'brand'}
                type={'submit'}
                isDisabled={!isFormFilled()}
              >
                Create
              </Button>
            </ModalFooter>
          </form>
        </OverlaySpinnerWrapper>
      </ModalContent>
    </Modal>
  );
}

AddUserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  header: PropTypes.string.isRequired,
  selectedCustomerId: PropTypes.string,
  companyNames: PropTypes.arrayOf(PropTypes.string),
  onSubmitSuccess: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
};

export default AddUserModal;
